import { Link } from 'react-router-dom';
import type { Payment } from '@portal-contract';
import { TableBase, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from '@/lib/macondo';
import { useI18n } from '@/i18n/I18nProvider';
import { useIsWide } from '@/hooks/useMediaQuery';
import { formatDate, formatMoney } from '@/lib/format';
import { Card } from './Card';
import { Pair } from './InvoiceList';

const invoicePath = (cid: string, iid: string): string => `/a/${encodeURIComponent(cid)}/invoices/${encodeURIComponent(iid)}`;

function Applied({ payment, cid }: { payment: Payment; cid: string }) {
  const { t, lang } = useI18n();
  const hasUnapplied = payment.unapplied.amount > 0;
  if (payment.applied.length === 0 && !hasUnapplied) return <span>-</span>;
  return (
    <ul className="flex flex-col gap-050">
      {payment.applied.map((a) => (
        <li key={a.invoiceId}>
          <Link to={invoicePath(cid, a.invoiceId)} className="focus-ring text-typography-primary inline-flex min-h-[32px] items-center underline">
            {a.docNumber}
          </Link>
          <span className="text-typography-secondary tabular-nums"> {formatMoney(a.amount, lang)}</span>
        </li>
      ))}
      {hasUnapplied && <li className="text-typography-secondary">{t('payments.unapplied', { amount: formatMoney(payment.unapplied, lang) })}</li>}
    </ul>
  );
}

/** Payments as a table from 640 px and as cards below it. */
export function PaymentList({ items, cid, caption }: { items: Payment[]; cid: string; caption: string }) {
  const { t, lang } = useI18n();
  const wide = useIsWide();

  if (wide) {
    return (
      <Card className="overflow-hidden">
        <TableBase>
          <TableCaption className="sr-only">{caption}</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">{t('payments.col.date')}</TableHead>
              <TableHead scope="col" className="text-right">
                {t('payments.col.amount')}
              </TableHead>
              <TableHead scope="col">{t('payments.col.reference')}</TableHead>
              <TableHead scope="col">{t('payments.col.applied')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((p) => (
              <TableRow key={p.id}>
                <TableCell>{formatDate(p.date, lang)}</TableCell>
                <TableCell className="text-right font-medium tabular-nums">{formatMoney(p.amount, lang)}</TableCell>
                <TableCell>{p.reference ?? '-'}</TableCell>
                <TableCell>
                  <Applied payment={p} cid={cid} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </TableBase>
      </Card>
    );
  }

  return (
    <ul className="flex flex-col gap-300" aria-label={caption}>
      {items.map((p) => (
        <li key={p.id}>
          <Card className="p-400">
            <dl className="text-callout grid grid-cols-2 gap-x-400 gap-y-200">
              <Pair label={t('payments.col.date')} value={formatDate(p.date, lang)} />
              <Pair label={t('payments.col.amount')} value={formatMoney(p.amount, lang)} strong />
              <Pair label={t('payments.col.reference')} value={p.reference ?? '-'} />
            </dl>
            <div className="mt-200">
              <p className="text-footnote text-typography-secondary">{t('payments.col.applied')}</p>
              <div className="text-callout">
                <Applied payment={p} cid={cid} />
              </div>
            </div>
          </Card>
        </li>
      ))}
    </ul>
  );
}
